import { useRef, useEffect, useImperativeHandle, forwardRef, useState } from 'react';
import { OrgChart as D3OrgChart } from 'd3-org-chart';
import { select } from 'd3-selection';
import { useOrgData } from '../../contexts/OrgDataContext';
import { Employee, Division, DivisionKey } from '../../types/orgchart.types';
import { Tooltip } from './Tooltip';
import './OrgChart.css';

export interface OrgChartRef {
  fit: () => void;
  expandAll: () => void;
  collapseAll: () => void;
  highlightNode: (id: string) => void;
  exportPng: () => Promise<string>;
  exportSvg: () => Promise<string>;
}

interface OrgChartProps {
  onNodeClick?: (nodeId: string) => void;
}

// Node content template - using inline styles for export compatibility
const createNodeContent = (
  divisions: Record<DivisionKey, Division>,
  showVfp: boolean
) => (d: any) => {
  const data = d.data;
  const division = divisions[data.division as DivisionKey];
  const color = division?.color || '#3b82f6';
  const icon = division?.icon || '👤';
  const subordinates = d._directSubordinates || 0;
  const totalSubs = d._totalSubordinates || 0;
  const hasDepartments = data.departments && data.departments.length > 0;

  // Company VFP banner at top
  if (data.isCompanyVfp) {
    return `
      <div
        class="org-node-card company-vfp-card"
        data-employee-id="${data.id}"
        style="
          width:${d.width}px;
          min-height:${d.height}px;
          background:linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
          border:3px solid #3b82f6;
          border-radius:16px;
          overflow:hidden;
          cursor:pointer;
          box-shadow:0 8px 32px rgba(0,0,0,0.2);
          direction:rtl;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          padding:20px;
        ">
        <div style="font-size:24px; margin-bottom:8px;">🏗️</div>
        <div style="font-weight:800; font-size:20px; color:#ffffff; text-align:center; margin-bottom:8px;">${data.name}</div>
        <div style="font-size:11px; color:#93c5fd; font-weight:600; text-transform:uppercase; letter-spacing:1px; margin-bottom:6px;">תוצר סופי בעל ערך</div>
        <div style="font-size:14px; color:#e0f2fe; font-weight:600; text-align:center; line-height:1.5;">${data.vfp}</div>
      </div>
    `;
  }

  // Board of Directors card
  if (data.isBoard) {
    return `
      <div
        class="org-node-card board-card"
        data-employee-id="${data.id}"
        style="
          width:${d.width}px;
          min-height:${d.height}px;
          background:linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border:2px solid #f59e0b;
          border-radius:12px;
          overflow:hidden;
          cursor:pointer;
          box-shadow:0 4px 16px rgba(0,0,0,0.1);
          direction:rtl;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          padding:16px;
        ">
        <div style="font-size:28px; margin-bottom:6px;">🏛️</div>
        <div style="font-weight:700; font-size:16px; color:#92400e; text-align:center;">${data.name}</div>
        ${data.vfp ? `<div style="font-size:11px; color:#b45309; margin-top:6px; text-align:center; line-height:1.4;">${data.vfp}</div>` : ''}
      </div>
    `;
  }

  // Owner card
  if (data.isOwner) {
    return `
      <div
        class="org-node-card owner-card"
        data-employee-id="${data.id}"
        style="
          width:${d.width}px;
          min-height:${d.height}px;
          background:linear-gradient(135deg, #fef9c3 0%, #fef08a 100%);
          border:2px solid #eab308;
          border-radius:12px;
          overflow:hidden;
          cursor:pointer;
          box-shadow:0 4px 16px rgba(0,0,0,0.1);
          direction:rtl;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          padding:16px;
        ">
        <div style="font-size:28px; margin-bottom:6px;">👑</div>
        <div style="font-weight:700; font-size:16px; color:#854d0e; text-align:center;">${data.name}</div>
        <div style="font-size:12px; color:#a16207; margin-top:2px;">בעלים</div>
        ${data.vfp ? `<div style="font-size:11px; color:#92400e; margin-top:6px; text-align:center; line-height:1.4;">${data.vfp}</div>` : ''}
      </div>
    `;
  }

  // CEO card
  if (data.isCeo) {
    return `
      <div
        class="org-node-card ceo-card"
        data-employee-id="${data.id}"
        style="
          width:${d.width}px;
          min-height:${d.height}px;
          background:#ffffff;
          border:2px solid ${color}60;
          border-radius:12px;
          overflow:hidden;
          cursor:pointer;
          transition:all 0.2s;
          box-shadow:0 4px 16px rgba(0,0,0,0.08);
          direction:rtl;
          display:flex;
          flex-direction:column;
        ">
        <div style="height:4px; background:linear-gradient(90deg, ${color}, ${color}80)"></div>
        <div style="padding:16px 20px; flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center;">
          <div style="width:48px; height:48px; background:${color}20; border:2px solid ${color}40; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:10px;">${icon}</div>
          <div style="font-weight:700; font-size:18px; color:#1e293b; text-align:center; margin-bottom:2px;">${data.name}</div>
          <div style="font-size:12px; color:#64748b; font-weight:500; text-align:center;">מנכ"ל</div>
        </div>
        ${data.vfp ? `<div style="background:linear-gradient(180deg, ${color}08 0%, ${color}15 100%); border-top:2px solid ${color}40; padding:10px 16px;">
          <div style="font-size:12px; color:#1e293b; font-weight:700; text-align:center;">${data.vfp}</div>
        </div>` : ''}
      </div>
    `;
  }

  // C-Level Manager card
  if (data.isCLevel) {
    return `
      <div
        class="org-node-card clevel-card"
        data-employee-id="${data.id}"
        style="
          width:${d.width}px;
          min-height:${d.height}px;
          background:#ffffff;
          border:2px solid #6366f1;
          border-radius:12px;
          overflow:hidden;
          cursor:pointer;
          box-shadow:0 4px 16px rgba(0,0,0,0.08);
          direction:rtl;
          display:flex;
          flex-direction:column;
        ">
        <div style="height:4px; background:linear-gradient(90deg, #6366f1, #8b5cf6)"></div>
        <div style="padding:14px 16px; flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center;">
          <div style="width:40px; height:40px; background:#eef2ff; border:2px solid #c7d2fe; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:20px; margin-bottom:8px;">💼</div>
          <div style="font-weight:700; font-size:15px; color:#1e293b; text-align:center;">${data.name}</div>
          <div style="font-size:11px; color:#6366f1; font-weight:600; margin-top:2px;">סמנכ״ל</div>
        </div>
        ${data.vfp ? `<div style="background:#eef2ff; border-top:1px solid #c7d2fe; padding:8px 12px;">
          <div style="font-size:10px; color:#4338ca; font-weight:600; text-align:center;">${data.vfp}</div>
        </div>` : ''}
      </div>
    `;
  }

  // Render embedded departments (for orgboard mode)
  const departmentsHtml = hasDepartments ? `
    <div style="
      margin-top:8px;
      padding-top:8px;
      border-top:1px dashed ${color}40;
    ">
      ${data.departments.map((dept: any) => `
        <div style="
          background:${color}08;
          border:1px solid ${color}20;
          border-radius:6px;
          padding:8px 10px;
          margin-bottom:6px;
        ">
          <div style="
            font-weight:600;
            font-size:11px;
            color:#1e293b;
            margin-bottom:2px;
          ">מחלקת ${dept.name} <span style="font-weight:400;color:#94a3b8;font-size:9px;">(${dept.number})</span></div>
          ${dept.manager ? `<div style="
            font-size:10px;
            color:#64748b;
            margin-bottom:2px;
          ">${dept.manager}</div>` : ''}
          ${dept.vfp ? `<div style="
            font-size:9px;
            font-weight:500;
            color:#475569;
            line-height:1.4;
            word-wrap:break-word;
            overflow-wrap:break-word;
          ">${dept.vfp}</div>` : ''}
        </div>
      `).join('')}
    </div>
  ` : '';

  return `
    <div
      class="org-node-card"
      data-employee-id="${data.id}"
      style="
        width:${d.width}px;
        min-height:${d.height}px;
        background:#ffffff;
        border:2px solid ${color}40;
        border-radius:12px;
        overflow:hidden;
        cursor:pointer;
        transition:all 0.2s;
        box-shadow:0 4px 16px rgba(0,0,0,0.08);
        direction:rtl;
        display:flex;
        flex-direction:column;
      ">
      <!-- Color Bar -->
      <div style="height:4px; background:linear-gradient(90deg, ${color}, ${color}80)"></div>

      <!-- Content -->
      <div style="padding:14px 16px; flex:1;">
        <!-- Header -->
        <div style="display:flex; align-items:center; gap:10px; margin-bottom:6px;">
          <div style="
            width:36px;
            height:36px;
            background:${color}20;
            border:1px solid ${color}40;
            border-radius:8px;
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:18px;
          ">${icon}</div>
          <div style="flex:1; min-width:0;">
            <div style="
              font-weight:700;
              font-size:14px;
              color:#1e293b;
            ">חטיבת ${data.name} <span style="font-weight:400;color:#94a3b8;font-size:11px;">(${data.divNum})</span></div>
            ${data.manager ? `<div style="
              font-size:11px;
              color:#64748b;
            ">${data.manager}</div>` : ''}
          </div>
        </div>

        <!-- Embedded Departments -->
        ${departmentsHtml}
      </div>

      <!-- VFP Bottom Section -->
      ${data.vfp ? `<div style="
        margin-top:auto;
        background:linear-gradient(180deg, ${color}08 0%, ${color}15 100%);
        border-top:2px solid ${color}40;
        padding:10px 16px;
      ">
        <div style="
          display:flex;
          align-items:center;
          justify-content:center;
          gap:8px;
          margin-bottom:4px;
        ">
          <div style="flex:1;height:1px;background:linear-gradient(90deg, transparent, ${color}60)"></div>
          <div style="font-size:9px;color:${color};font-weight:600;text-transform:uppercase;letter-spacing:1px;">תוצר סופי</div>
          <div style="flex:1;height:1px;background:linear-gradient(270deg, transparent, ${color}60)"></div>
        </div>
        <div style="
          font-size:14px;
          font-weight:700;
          color:#1e293b;
          text-align:center;
          line-height:1.4;
        ">${data.vfp}</div>
      </div>` : ''}
    </div>
  `;
};

// Button content template
const createButtonContent = (divisions: Record<DivisionKey, Division>) => ({ node }: any) => {
  const color = divisions[node.data.division as DivisionKey]?.color || '#3b82f6';
  const icons = {
    expanded: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M18 15l-6-6-6 6"/></svg>`,
    collapsed: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>`
  };

  return `<div style="
    width:28px;
    height:28px;
    border-radius:50%;
    background:${color};
    display:flex;
    align-items:center;
    justify-content:center;
    cursor:pointer;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);
  ">${node.children ? icons.expanded : icons.collapsed}</div>`;
};

export const OrgChart = forwardRef<OrgChartRef, OrgChartProps>(({ onNodeClick }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const { getFilteredEmployees, divisions, showVfp, highlightedEmployeeId, searchQuery, employees, maxLevel, layout, viewMode } = useOrgData();

  // Tooltip state
  const [hoveredEmployee, setHoveredEmployee] = useState<Employee | null>(null);
  const [hoveredManager, setHoveredManager] = useState<Employee | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const currentCardIdRef = useRef<string | null>(null);
  const prevMaxLevelRef = useRef<number>(-1); // Start with -1 to trigger on first render

  const visibleEmployees = getFilteredEmployees();

  // Initialize chart
  useEffect(() => {
    if (!containerRef.current) return;

    if (!chartRef.current) {
      chartRef.current = new D3OrgChart();
    }

    chartRef.current
      .container(containerRef.current)
      .data(visibleEmployees)
      .nodeId((d: any) => d.id)
      .parentNodeId((d: any) => d.pid)
      .nodeWidth(() => 340)
      .nodeHeight((d: any) => {
        // Dynamic height based on whether node has embedded departments
        const hasDepartments = d.data?.departments && d.data.departments.length > 0;
        if (hasDepartments) {
          // Base height + space for each department (~120px each for full VFP text)
          return 160 + (d.data.departments.length * 120);
        }
        return 140;
      })
      .childrenMargin(() => 50)
      .compactMarginBetween(() => 35)
      .compactMarginPair(() => 50)
      .neighbourMargin(() => 20)
      .siblingsMargin(() => 20)
      .nodeContent(createNodeContent(divisions, showVfp))
      .buttonContent(createButtonContent(divisions))
      .linkUpdate(function(this: any) {
        select(this)
          .attr('stroke', '#cbd5e1')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '0')
          .attr('opacity', 0.8);
      })
      // In 'top' layout: disable compact for single row of divisions
      // In 'left' layout: enable compact to minimize vertical space
      .compact(layout === 'left')
      .layout(layout)
      .setActiveNodeCentered(true)
      .onNodeClick((d: any) => {
        if (onNodeClick) {
          onNodeClick(d.data.id);
        }
      })
      .render();

    // In orgboard mode, always expand all nodes to show all departments
    // In personnel mode, apply depth filter based on maxLevel
    const chartState = chartRef.current.getChartState();
    const allNodes = chartState.allNodes;

    if (allNodes) {
      if (viewMode === 'orgboard') {
        // Expand all in org board mode
        allNodes.forEach((node: any) => {
          node.data._expanded = true;
        });
        chartRef.current.render().fit();
      } else if (prevMaxLevelRef.current !== maxLevel) {
        // Only apply depth filter in personnel mode when maxLevel changes
        prevMaxLevelRef.current = maxLevel;
        allNodes.forEach((node: any) => {
          if (maxLevel >= 10) {
            node.data._expanded = true;
          } else if (node.depth < maxLevel) {
            node.data._expanded = true;
          } else {
            node.data._expanded = false;
          }
        });
        chartRef.current.render().fit();
      }
    }
  }, [visibleEmployees, divisions, showVfp, onNodeClick, maxLevel, layout, viewMode]);

  // Event delegation for tooltip - using mouseover/mouseout
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const card = target.closest('.org-node-card') as HTMLElement;

      // Don't show tooltip when hovering over expand/collapse buttons (outside cards)
      if (!card && (target.tagName === 'circle' || target.tagName === 'path' || target.closest('g[cursor="pointer"]'))) {
        currentCardIdRef.current = null;
        setTooltipVisible(false);
        return;
      }

      if (card) {
        const employeeId = card.getAttribute('data-employee-id');
        if (employeeId && employeeId !== currentCardIdRef.current) {
          currentCardIdRef.current = employeeId;
          const employee = employees.find(e => e.id === employeeId);
          if (employee) {
            const manager = employee.pid ? employees.find(e => e.id === employee.pid) : null;
            setHoveredEmployee(employee);
            setHoveredManager(manager || null);
            setTooltipPosition({ x: event.pageX, y: event.pageY });
            setTooltipVisible(true);
          }
        } else if (employeeId === currentCardIdRef.current) {
          // Update position while staying on same card
          setTooltipPosition({ x: event.pageX, y: event.pageY });
        }
      } else {
        // Mouse is not over any card - hide tooltip
        currentCardIdRef.current = null;
        setTooltipVisible(false);
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      // If mouse left the container entirely, hide tooltip
      if (!relatedTarget || !container.contains(relatedTarget)) {
        currentCardIdRef.current = null;
        setTooltipVisible(false);
      }
    };

    // Hide tooltip on any click (for expand/collapse)
    const handleClick = () => {
      currentCardIdRef.current = null;
      setTooltipVisible(false);
    };

    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseout', handleMouseOut);
    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('mouseover', handleMouseOver);
      container.removeEventListener('mouseout', handleMouseOut);
      container.removeEventListener('click', handleClick);
    };
  }, [employees]);

  // Handle highlighted node
  useEffect(() => {
    if (!chartRef.current || !highlightedEmployeeId) return;

    try {
      chartRef.current.setHighlighted(highlightedEmployeeId).render();
      chartRef.current.setUpToTheRootHighlighted(highlightedEmployeeId).render();
    } catch (e) {
      console.warn('Failed to highlight node:', e);
    }
  }, [highlightedEmployeeId]);

  // Handle search
  useEffect(() => {
    if (!chartRef.current) return;

    const q = searchQuery.toLowerCase().trim();

    if (!q) {
      try {
        chartRef.current.clearHighlighting();
      } catch (e) {
        // Ignore if clearHighlighting not available
      }
      return;
    }

    // Find matching employees (search by name and division)
    const matchingIds = employees
      .filter(e =>
        e.name.toLowerCase().includes(q) ||
        (e.division && e.division.toLowerCase().includes(q))
      )
      .map(e => e.id);

    if (matchingIds.length > 0) {
      try {
        chartRef.current.setHighlighted(matchingIds[0]).render();
        chartRef.current.setCentered(matchingIds[0]).render();
      } catch (e) {
        console.warn('Failed to highlight search result:', e);
      }
    }
  }, [searchQuery, employees]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    fit: () => {
      chartRef.current?.fit();
    },
    expandAll: () => {
      chartRef.current?.expandAll();
    },
    collapseAll: () => {
      chartRef.current?.collapseAll();
    },
    exportPng: () => {
      return new Promise((resolve, reject) => {
        if (!chartRef.current) {
          reject(new Error('Chart not initialized'));
          return;
        }

        // Use scale of 4 - good quality while staying under browser canvas limits (~16384px)
        // Higher scales on wide charts can exceed limits and get cut off
        const dynamicScale = 4;

        // Use d3-org-chart's exportImg
        chartRef.current.exportImg({
          full: true,
          scale: dynamicScale,
          save: false,
          onLoad: (base64: string) => {
            resolve(base64);
          }
        });
      });
    },
    exportSvg: () => {
      return new Promise((resolve, reject) => {
        if (!chartRef.current) {
          reject(new Error('Chart not initialized'));
          return;
        }

        // Get chart state which contains node positions
        const chartState = chartRef.current.getChartState();
        const svg = chartState.svg;

        if (!svg) {
          reject(new Error('SVG not found in chart state'));
          return;
        }

        // Get the SVG node
        const svgNode = svg.node() as SVGSVGElement;
        if (!svgNode) {
          reject(new Error('SVG node not found'));
          return;
        }

        // Get all nodes to calculate full bounds
        const allNodes = chartState.allNodes || [];
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        allNodes.forEach((node: any) => {
          const x = node.x || 0;
          const y = node.y || 0;
          const width = node.width || 260;
          const height = node.height || 170;

          minX = Math.min(minX, x - width / 2);
          minY = Math.min(minY, y - height / 2);
          maxX = Math.max(maxX, x + width / 2);
          maxY = Math.max(maxY, y + height / 2);
        });

        // Add padding
        const padding = 50;
        minX -= padding;
        minY -= padding;
        maxX += padding;
        maxY += padding;

        const fullWidth = maxX - minX;
        const fullHeight = maxY - minY;

        // Clone the SVG and set new viewBox for full content
        const clonedSvg = svgNode.cloneNode(true) as SVGSVGElement;
        clonedSvg.setAttribute('viewBox', `${minX} ${minY} ${fullWidth} ${fullHeight}`);
        clonedSvg.setAttribute('width', String(fullWidth));
        clonedSvg.setAttribute('height', String(fullHeight));

        // Serialize to string
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(clonedSvg);

        resolve(svgString);
      });
    },
    highlightNode: (id: string) => {
      chartRef.current?.setHighlighted(id).render();
      chartRef.current?.setUpToTheRootHighlighted(id).render();
    }
  }), []);

  return (
    <>
      <div ref={containerRef} className="org-chart-container" />
      <Tooltip
        employee={hoveredEmployee}
        divisions={divisions}
        position={tooltipPosition}
        visible={tooltipVisible}
        manager={hoveredManager}
      />
    </>
  );
});

OrgChart.displayName = 'OrgChart';
